import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';  // Добавете useNavigate
import CountUp from 'react-countup';
import { ArrowRight, Heart, Calendar, Users } from 'lucide-react';
import { stats, events as allEvents } from '../data/siteData';
import backgroundImage from '../assets/images/National94.webp';
import missionImage from '../assets/images/sternotom4.webp';

  // Взимаме само събитията с isFeatured: true за началната страница
  const HomePage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();  // Добавете този ред
    const featuredEvents = allEvents.filter(event => event.isFeatured === true);
    const latestEvents = featuredEvents.slice(0, 2);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black/80" />
        
        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <h1 className="font-heading text-7xl md:text-9xl font-bold text-white mb-6 leading-none" data-testid="hero-title">
              Nationals '94
            </h1>
            <p className="text-2xl md:text-3xl text-gray-200 font-light tracking-wide" data-testid="hero-tagline">
              {t('hero.tagline')}
            </p>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-lg md:text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to={`/${i18n.language}/donate`}
              data-testid="hero-donate-button"
              className="inline-flex items-center justify-center space-x-2 bg-white text-black font-semibold px-10 py-5 hover:bg-gray-200 transition-all duration-300 group"
            >
              <Heart className="w-5 h-5" />
              <span>{t('hero.cta')}</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to={`/${i18n.language}/about`}
              className="inline-flex items-center justify-center space-x-2 border-2 border-white text-white font-semibold px-10 py-5 hover:bg-white hover:text-black transition-all duration-300"
            >
              <span>{i18n.language === 'bg' ? 'Научете повече' : 'Learn More'}</span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Impact Stats Section */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4" data-testid="impact-title">
              {t('impact.title')}
            </h2>
            <p className="text-lg text-muted-foreground">
              {i18n.language === 'bg' ? 'Създаваме реална промяна в живота на младите футболисти' : 'Creating real change in the lives of young footballers'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.7 }}
              className="bg-card border border-border p-8 hover:shadow-lg transition-shadow"
              data-testid="stat-kids"
            >
              <Users className="w-12 h-12 text-foreground mb-4" />
              <div className="font-heading text-5xl font-bold text-foreground mb-2">
                <CountUp end={stats.youth_players} duration={2.5} separator="," />
              </div>
              <p className="text-lg text-muted-foreground">{t('impact.kids')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.7 }}
              className="bg-card border border-border p-8 hover:shadow-lg transition-shadow"
              data-testid="stat-funds"
            >
              <Heart className="w-12 h-12 text-foreground mb-4" />
              <div className="font-heading text-5xl font-bold text-foreground mb-2">
                €<CountUp end={stats.funds_raised} duration={2.5} separator="," />
              </div>
              <p className="text-lg text-muted-foreground">{t('impact.funds')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="bg-card border border-border p-8 hover:shadow-lg transition-shadow"
              data-testid="stat-events"
            >
              <Calendar className="w-12 h-12 text-foreground mb-4" />
              <div className="font-heading text-5xl font-bold text-foreground mb-2">
                <CountUp end={stats.annual_events} duration={2.5} />
              </div>
              <p className="text-lg text-muted-foreground">{t('impact.events')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="py-24 bg-muted">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              {i18n.language === 'bg' ? 'Последни новини' : 'Latest News'}
            </h2>
            <p className="text-lg text-muted-foreground">
              {i18n.language === 'bg' ? 'Следете последните новини и събития от фондацията' : 'Stay updated with our latest news and events'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {latestEvents.map((event, index) => (
              <motion.div
            key={event.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2, duration: 0.7 }}
            className="bg-card border border-border overflow-hidden group hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate(`/${i18n.language}/events/${event.id}`)}  // Променете този ред
          >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={event.image}
                    alt={i18n.language === 'bg' ? event.title_bg : event.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-accent text-accent-foreground px-3 py-1 font-bold uppercase text-xs">
                    {event.category}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-3">
                    <Calendar className="w-4 h-4" />
                    <span>{event.date}</span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-foreground mb-3 group-hover:text-accent transition-colors">
                    {i18n.language === 'bg' ? event.title_bg : event.title}
                  </h3>
                  <p className="text-muted-foreground line-clamp-2">
                    {i18n.language === 'bg' ? event.description_bg : event.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to={`/${i18n.language}/events`}
              className="inline-flex items-center space-x-2 bg-primary text-primary-foreground font-medium px-8 py-4 hover:bg-accent hover:text-accent-foreground transition-all group"
            >
              <span>{i18n.language === 'bg' ? 'Всички събития' : 'All Events'}</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Section - Improved with proper image display */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6" data-testid="mission-title">
                {t('mission.title')}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                {t('mission.description')}
              </p>
              <blockquote className="text-xl text-foreground font-medium italic border-l-4 border-primary pl-6 mb-8">
                "{t('mission.vision')}"
              </blockquote>
              <Link
                to={`/${i18n.language}/about`}
                className="inline-flex items-center space-x-2 bg-primary text-primary-foreground font-medium px-8 py-4 hover:bg-accent hover:text-accent-foreground transition-all group"
              >
                <span>{i18n.language === 'bg' ? 'Научете повече' : 'Learn More'}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative group"
            >
              {/* Main image container with elegant frame */}
              <div className="relative shadow-2xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-3">
                <div className="relative overflow-hidden bg-white dark:bg-gray-800">
                  <img
                    src={missionImage}
                    alt="Mission"
                    className="w-full h-auto object-contain"
                  />
                  
                  {/* Inner border effect */}
                  <div className="absolute inset-0 border-2 border-white/20 pointer-events-none"></div>
                </div>
                
                {/* Outer decorative border */}
                <div className="absolute inset-0 border-2 border-primary/20 pointer-events-none"></div>
              </div>
              
              
            </motion.div>
          </div>
        </div>
      </section>

      {/* Support Us Section */}
      <section 
        className="py-24"
        style={{ backgroundColor: '#1a2332', color: 'white' }}
        data-testid="support-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6" style={{ color: 'white' }}>
              {i18n.language === 'bg' ? 'Подкрепете ни днес' : 'Support Us Today'}
            </h2>
            <p className="text-lg mb-10 leading-relaxed max-w-2xl mx-auto" style={{ color: '#d1d5db' }}>
              {i18n.language === 'bg'
                ? 'Всяко дарение, независимо от размера, помага за развитието на младите таланти и запазването на нашето наследство.'
                : 'Every donation, regardless of size, helps develop young talent and preserve our legacy.'}
            </p>
            <Link
              to={`/${i18n.language}/donate`}
              data-testid="support-donate-button"
              className="inline-flex items-center space-x-2 bg-white font-semibold px-10 py-5 hover:bg-gray-100 transition-all group"
              style={{ color: '#1a2332' }}
            >
              <Heart className="w-5 h-5" />
              <span>{t('hero.cta')}</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;